import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { RafflesFacade } from '@galabid/gb-dashboard/raffle/data-access'
import { TranslateModule } from '@ngx-translate/core'
import { ItemsEntity } from 'libs/gb-dashboard/item/data-access-selected-item/src/lib/+state/selected-item.models'
import { ProjectorNotificationData } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { Observable, take } from 'rxjs'
import { GbPresenterToolsTextComponent } from '../gb-presenter-tools-text/gb-presenter-tools-text.component'
import { GbPresenterToolsAdvancedOptionsContainerComponent } from '../gb-presenter-tools-advanced-options-container/gb-presenter-tools-advanced-options-container.component'

@Component({
  selector: 'gb-presenter-tools-notification',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    GbPresenterToolsTextComponent,
    GbPresenterToolsAdvancedOptionsContainerComponent,
  ],
  templateUrl: './gb-presenter-tools-notification.component.html',
  styleUrl: './gb-presenter-tools-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsNotificationComponent {
  @Input({ required: true }) toolsFormGroup!: FormGroup

  @Output() preview = new EventEmitter<ProjectorNotificationData>()

  private readonly TEST_NOTIFICATION: ProjectorNotificationData = {
    image: null,
    image_url: null,
    header: 'New Bid',
    body: 'Auction Item',
    secondary_text_1: 'John Smith',
    secondary_text_2: '$123',
    font_icon: null,
    notif_event: 'bid',
  }

  raffleItems$: Observable<ItemsEntity[]> = this.rafflesFacade.allRaffleItems$

  hideFields = ['text_align', 'font_style', 'color']

  notifTypeOptions = [
    { value: 'popout_a', label: 'PRESENTER.NOTIFICATION_TOOLS.NOTIF_TYPES.POPOUT_A' },
    { value: 'popout_b', label: 'PRESENTER.NOTIFICATION_TOOLS.NOTIF_TYPES.POPOUT_B' },
    { value: 'toast_a', label: 'PRESENTER.NOTIFICATION_TOOLS.NOTIF_TYPES.TOAST_A' },
  ]

  constructor(private rafflesFacade: RafflesFacade) {}

  previewNotification(): void {
    this.raffleItems$.pipe(take(1)).subscribe((items) => {
      const firstItem = items?.[0]
      if (!firstItem) {
        this.preview.emit(this.TEST_NOTIFICATION)
        return
      }

      const image = firstItem.images?.[0]?.image?.image ?? null
      const body = firstItem.name || this.TEST_NOTIFICATION.body

      this.preview.emit({
        ...this.TEST_NOTIFICATION,
        image,
        image_url: image,
        body,
      })
    })
  }

  get backgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('background_color') as FormControl
  }

  get notifSecondaryColorControl(): FormControl {
    return this.toolsFormGroup.get('notif_secondary_color') as FormControl
  }

  get notifSecondaryBackgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('notif_secondary_background_color') as FormControl
  }

  get notifTypeControl(): FormControl {
    return this.toolsFormGroup.get('notif_type') as FormControl
  }

  get showBidNotificationsControl(): FormControl {
    return this.toolsFormGroup.get('show_bid_notifications') as FormControl
  }

  get showPurchaseNotificationsControl(): FormControl {
    return this.toolsFormGroup.get('show_purchase_notifications') as FormControl
  }

  get showDonationNotificationsControl(): FormControl {
    return this.toolsFormGroup.get('show_donation_notifications') as FormControl
  }

  get showCustomNotificationsControl(): FormControl {
    return this.toolsFormGroup.get('show_custom_notifications') as FormControl
  }

  get hideNameControl(): FormControl {
    return this.toolsFormGroup.get('hide_name') as FormControl
  }

  get hidePriceControl(): FormControl {
    return this.toolsFormGroup.get('hide_price') as FormControl
  }

  get imageContainedControl(): FormControl {
    return this.toolsFormGroup.get('image_contained') as FormControl
  }
}
